import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
  PendingBackOfficeRow,
  PendingBondDefinitionRow,
  PendingDiscountNoteRow,
  PendingInitReopenBondRow,
  PendingItemsModel,
  PendingNewItemRow,
  PendingNoVerifiedBondRow,
  PendingRepurcBondRow,
  PendingSwapRow,
} from './dashboard.types';
import { apiUrl } from '../../auth/api-url';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly endpoint = apiUrl('PendingItem');

  constructor(private readonly http: HttpClient) {}

  getPendingItems(): Observable<PendingItemsModel> {
    return this.http
      .get<unknown>(this.endpoint)
      .pipe(map((response) => this.normalize(response)));
  }

  private normalize(response: unknown): PendingItemsModel {
    const source = this.toRecord(response);

    return {
      alertDate: this.text(source, 'alertDate'),
      newItems: this.list(source, 'newItems').map((item) => this.toNewItem(item)),
      pendingInitReopenBonds: this.list(source, 'pendingInitReopenBonds').map((item) =>
        this.toInitReopenBond(item),
      ),
      pendingDiscountNotes: this.list(source, 'pendingDiscountNotes').map((item) =>
        this.toDiscountNote(item),
      ),
      pendingSwaps: this.list(source, 'pendingSwaps').map((item) => this.toSwap(item)),
      pendingRepurcBonds: this.list(source, 'pendingRepurcBonds').map((item) =>
        this.toRepurcBond(item),
      ),
      pendingBondDefinitions: this.list(source, 'pendingBondDefinitions').map((item) =>
        this.toBondDefinition(item),
      ),
      pendingBackOffices: this.list(source, 'pendingBackOffices').map((item) =>
        this.toBackOffice(item),
      ),
      noVerifiedBonds: this.list(source, 'noVerifiedBonds').map((item) =>
        this.toNoVerifiedBond(item),
      ),
    };
  }

  private toNewItem(item: Record<string, unknown>): PendingNewItemRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      securityType: this.text(item, 'securityType'),
      tradeType: this.text(item, 'tradeType'),
    };
  }

  private toInitReopenBond(item: Record<string, unknown>): PendingInitReopenBondRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      type: this.text(item, 'type'),
      proceeds: this.text(item, 'proceeds'),
      documentation: this.text(item, 'documentation'),
      fndCost: this.text(item, 'fndCost'),
      investorAllocation: this.text(item, 'investorAllocation'),
      leads: this.text(item, 'leads'),
      arbitrage: this.text(item, 'arbitrage'),
    };
  }

  private toDiscountNote(item: Record<string, unknown>): PendingDiscountNoteRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      documentation: this.text(item, 'documentation'),
      investorAllocation: this.text(item, 'investorAllocation'),
    };
  }

  private toSwap(item: Record<string, unknown>): PendingSwapRow {
    return {
      ...this.toBase(item),
      structureId: this.text(item, 'structureId'),
      rfrTradeId: this.text(item, 'rfrTradeId'),
      purpose: this.text(item, 'purpose'),
      purposeSubtype: this.text(item, 'purposeSubtype'),
    };
  }

  private toRepurcBond(item: Record<string, unknown>): PendingRepurcBondRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      repurch: this.text(item, 'repurch'),
      refinance: this.text(item, 'refinance'),
      spread: this.text(item, 'spread'),
    };
  }

  private toBondDefinition(item: Record<string, unknown>): PendingBondDefinitionRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      documentation: this.text(item, 'documentation'),
      proceeds: this.text(item, 'proceeds'),
      format: this.text(item, 'format'),
    };
  }

  private toBackOffice(item: Record<string, unknown>): PendingBackOfficeRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      type: this.text(item, 'type'),
      bondPricingSupplement: this.text(item, 'bondPricingSupplement'),
      tranche: this.text(item, 'tranche'),
      hasLegalDocument: this.text(item, 'hasLegalDocument'),
      hasTermSheet: this.text(item, 'hasTermSheet'),
      hasRta: this.text(item, 'hasRta'),
    };
  }

  private toNoVerifiedBond(item: Record<string, unknown>): PendingNoVerifiedBondRow {
    return {
      ...this.toBase(item),
      securityId: this.text(item, 'securityId'),
      securityType: this.text(item, 'securityType'),
      tradeType: this.text(item, 'tradeType'),
    };
  }

  private toBase(item: Record<string, unknown>) {
    return {
      tradeId: this.text(item, 'tradeId'),
      dealId: this.text(item, 'dealId'),
      tradeDate: this.text(item, 'tradeDate'),
      doneUser: this.text(item, 'doneUser'),
    };
  }

  private list(source: Record<string, unknown>, key: string): Record<string, unknown>[] {
    const value = this.readValue(source, key);
    if (!Array.isArray(value)) {
      return [];
    }

    return value.filter((entry): entry is Record<string, unknown> => this.isRecord(entry));
  }

  private text(source: Record<string, unknown>, key: string): string {
    const value = this.readValue(source, key);

    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    return '';
  }

  /** Reads a property tolerating PascalCase/camelCase differences from the API. */
  private readValue(source: Record<string, unknown>, key: string): unknown {
    if (key in source) {
      return source[key];
    }

    const lowerKey = key.toLowerCase();
    const matchedKey = Object.keys(source).find((candidate) => candidate.toLowerCase() === lowerKey);

    return matchedKey === undefined ? undefined : source[matchedKey];
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return this.isRecord(value) ? value : {};
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
  }
}
